// Per-domain backend switching for the Tier-B replacement surface.
//
// apiHostFor lets an EXISTING Rails-equivalent endpoint be flip-tested against
// the ACH Go backend, one domain at a time, without a backend-wide swap. A hook
// that has been certified against ACH threads `apiHostFor('contracts')` instead
// of `apiHost`; every other hook keeps using `apiHost` unchanged.
//
// A domain routes to ACH iff (a) it is in the checked-in migrated-domain list
// AND (b) the env gate (achBackendEnabled) is open. Otherwise it returns the
// Ventura host — so an unflipped domain has ZERO behaviour change.

export type AppEnv = 'development' | 'sandbox' | 'production' | 'qa';

// Domains that MAY be served by ACH. A domain being listed here does not by
// itself flip it — the hook must also opt in by calling apiHostFor, and the env
// gate must be open. Custom segments, shared app lists, pixels and reporting hub
// are intentionally absent: ACH does not serve them.
export type AchDomain =
  | 'contracts'
  // The two ACH-served insertion-order reads ONLY: the contract-nested index
  // (/contracts/{id}/insertion_orders) and the name autocomplete
  // (/insertion_orders/autocomplete_insertion_order_name). IO SHOW
  // (/insertion_orders/{id}, consumed by useLineItem + JumpTo), `new`, versions,
  // and all write/reporting verbs STAY on Rails (DynamoDB InsertionOrderSpend +
  // un-migrated campaigns/delivery_profiles children — docs/dispositions/
  // insertionorder.md). Those consumers keep calling `apiHost` directly.
  | 'insertionOrders'
  // The two ACH-served campaign (== "AdGroup" in this FE) reads ONLY: the
  // IO-nested index (/insertion_orders/{id}/campaigns, consumed by useAdGroups →
  // ContractNav/LineItemNav + AdGroupSelector) and the campaign SHOW
  // (/campaigns/{id}, consumed by useAdGroup → AdGroupPage/ProductPage edit-load).
  // Everything else campaign-shaped STAYS on Rails and keeps calling `apiHost`:
  // all writes (create/update/copy/paste/pause_toggle/comment), `new` +
  // targeting_defaults, versions/versions_meta (blocked — un-migrated
  // Admin::TargetingProfile branch), campaign_state_string, tile_estimate,
  // products/inhand, the 11 Malibu/Venice external status verbs, and the
  // further-nested locations/destinations/location_lists/targeted_creatives
  // controllers. The contract-nested campaigns index
  // (/contracts/{id}/campaigns, useContractCampaigns) is a DIFFERENT route with
  // no ACH handler and also stays Rails. Grounding: ACH
  // internal/transport/http/campaign/register.go (2 GET registrations) +
  // docs/dispositions/campaign.md (24 routes — 2 port, 22 stays-Rails).
  | 'campaigns'
  | 'targeting'
  // The two ACH-served creatives reads ONLY: the advertiser-nested index
  // (/advertisers/{id}/creatives, consumed by CreativesList/CreativesDataGrid)
  // and the creative SHOW (/creatives/{id}, consumed by CreativesForm's
  // edit-load). The IO-nested targeted_creatives route has NO reachable FE
  // consumer (its only caller is dead code with a campaign-only path — settled
  // 2026-07-21) and creative_basic_info has no FE consumer either; both STAY on
  // Rails, and all creative write/reporting verbs keep calling `apiHost`.
  | 'creatives'
  | 'products'
  | 'advertisers'
  | 'clients'
  // The four role-list reads only (/admin/users/{managers,sales,traffickers});
  // the clients/{id}/users index and users/{id} show are NOT ACH-served.
  | 'users'
  // The contract changelog only (/admin/contracts/{id}/versions[_meta]); the
  // line-item / ad-group / creative changelogs are NOT ACH-served.
  | 'versions';

// Domains certified as ACH-served and eligible to flip. Starts EMPTY: with an
// empty list every domain resolves to Ventura, so behaviour is byte-for-byte
// unchanged in every env. Adding a domain here is the ENTIRE config-only flip —
// the host resolution (apiHostFor) and the path rewrite (toAchApiPath, applied
// in the fetch chokepoint) are already wired, so a listed domain's opted-in read
// hooks route to ACH's `/api/admin/v2` surface automatically when the env gate
// is open (QA by default).
//
// FLIPPED domains (each certified live-pair GREEN before entering this list):
// contracts (ACH-hywe, 2026-07-17), users role lists + contract versions
// (ACH-colk, 2026-07-20), clients + advertisers (ACH-colk re-cert 2026-07-20:
// UTC-Z render fix ACH#333 + RD-12 ratified nested-client shape). Adding a
// domain requires (a) the consumer opting in via `apiHostFor('<domain>')`,
// (b) v2 convention adoption (D2), and (c) a 0-unexpected-diffs live cert —
// do NOT add a domain here until all three hold.
// FLIP BATCH 1 (ACH-colk): users + versions certified FULL GREEN vs live
// ventura QA + ACH dev (2026-07-20 pair captures: 0 unexpected envelope diffs,
// UI invariants pass) and flipped here. clients + advertisers were CERTIFIED
// WITH FINDINGS and are deliberately EXCLUDED pending a ruling: ACH renders
// client timestamps EST-offset where the live clients controller renders UTC-Z
// (same instant; Rails set_time_zone is contracts/IOs-only), and ACH's nested
// advertiser `client` uses the ClientSerializer shape where Rails embeds the
// raw column dump (6 extra columns, no client_driven_defaults). All FE-read
// fields match on both; the exclusions are evidence-bar, not crash-risk.
// FLIP BATCH 3 (ACH-punh): insertionOrders — the two ACH-served IO reads (the
// contract-nested index + the name autocomplete) certified live-pair GREEN vs
// ventura QA + ACH dev (2026-07-21: 34-row index pair, 0 unexpected envelope
// diffs, RD-3 pagination + ACH-3our total_impressions ratified, ContractNav
// consumed the flipped response live). IO show/new/versions/writes stay Rails.
// FLIP BATCH 2 (ACH-czqd): creatives — the two ACH-served reads (advertiser-
// nested index + creative show) certified live-pair GREEN vs ventura QA + ACH
// dev (2026-08-04: video/ica/moment pairs, 0 unexpected envelope diffs). The two
// serializer divergences that had blocked it are fixed on ACH dev (association→
// null on scope-less reads + append_ias_internal_pixel emitted, both byte-equal),
// and video_ad.transcoding_start's offset-vs-Z rendering is ratified under the
// common RD-TZ rule (same instant; USER-ruled 2026-08-03). Creative
// write/reporting verbs + the unreachable targeted_creatives/creative_basic_info
// routes stay Rails.
// FLIP BATCH 3 / FINAL read surface (ACH-vrgd): campaigns — the two ACH-served
// campaign (== "AdGroup") reads (the IO-nested index + the campaign show)
// certified live-pair GREEN vs ventura QA + ACH dev (2026-08-05, ACH dev commit
// 12269032): contract 41284 / IO 200448 5-row index pair and the campaign-433215
// show pair are BYTE-EQUAL after the RD-2 {model} unwrap (53 keys, 4724 bytes
// both sides), 0 unexpected envelope diffs, only RD-3 pagination meta ratified.
// The ContractNav rail rendered the flipped index live (9/9 nav titles) and the
// Ad Group Name field populated identically, so useAdGroups/useAdGroup both
// consume ACH end-to-end. All campaign writes, `new`/targeting_defaults,
// versions, campaign_state_string, tile_estimate, products, the Malibu/Venice
// status verbs and the nested locations/destinations controllers stay Rails.
// COVERAGE NOTE: the QA cert corpus (advertiser 17303, 11 campaigns) carries
// EMPTY annotated_profile_targeting / annotated_blacklisted_segments /
// blacklisted_segments / pmp_data, so those embeds certified as equal-and-empty
// rather than positively exercised; their populated paths are covered by the
// ACH-side oracles (ACH-1bw1 #399, ACH-c0kw #401, ACH-ryvr #404).
export const ACH_MIGRATED_DOMAINS: readonly AchDomain[] = ['contracts', 'users', 'versions', 'clients', 'advertisers', 'insertionOrders', 'creatives', 'campaigns'];

// ACH mounts its entire admin API under this base path (Go: `apiBasePath` in
// cmd/ach/serve/server.go — everything is `/api/admin/v2/...`). Ventura serves
// the same resources UNPREFIXED under `/admin/...` (e.g. `/admin/contracts/123`
// vs ACH `/api/admin/v2/contracts/123` — note ACH drops the `/admin` segment).
// The ACH host map values are bare hosts, so a Tier-B call flipped to the ACH
// host would 404 unless its path is rewritten. This is the grounded resolution
// of design verify-item V2 ("`/api/admin/v2` + `/admin/contracts` vs stripped
// `/admin`"): the answer is STRIPPED — strip the leading `/admin` and prepend
// the base.
export const ACH_API_BASE_PATH = '/api/admin/v2';

// stripAchTrailingSlash removes a single slash sitting at the END of the path
// segment (immediately before the query/hash, or end of string). ACH's Go router
// does NOT match a trailing slash: live against ACH dev, `/api/admin/v2/contracts/`
// 404s ("page not found", pre-auth) where `/api/admin/v2/contracts` 401/200s. The
// contracts index consumer builds its path from the `/admin/contracts/*` splat
// route, which yields `/admin/contracts/?query` — the trailing slash Rails
// tolerates but ACH rejects. Mid-path slashes (e.g. `/contracts/42`) are
// untouched; only the terminal path slash is collapsed.
const stripAchTrailingSlash = (achPath: string): string => {
  const q = achPath.search(/[?#]/);
  const pathPart = q === -1 ? achPath : achPath.slice(0, q);
  const rest = q === -1 ? '' : achPath.slice(q);
  const trimmed = pathPart.endsWith('/') ? pathPart.slice(0, -1) : pathPart;
  return trimmed + rest;
};

// toAchApiPath maps a Ventura request path to its ACH equivalent. It is applied
// in the fetch chokepoint (services.tsx) ONLY for ACH hosts, so Ventura calls
// are never touched. Idempotent and conservative:
//   - already `/api/admin/v2/...`  → returned unchanged (never double-prefixed;
//     the Tier-A client already builds fully-prefixed paths).
//   - `/admin/...` (Ventura convention) → strip `/admin`, prepend the base, and
//     drop any terminal path slash ACH's router would 404 on (see above).
//   - anything else → returned unchanged (unknown shape: do not blindly prefix).
// The `/admin/` match requires the trailing slash so a path like
// `/administration/...` is never mangled.
export const toAchApiPath = (path: string): string => {
  const p = path || '';
  if (p.startsWith(ACH_API_BASE_PATH)) return p;
  if (p.startsWith('/admin/')) return stripAchTrailingSlash(`${ACH_API_BASE_PATH}${p.slice('/admin'.length)}`);
  return p;
};

// Env-gate default for the Tier-B replacement surface: ON only in QA (the first
// gated env, design §3.2), OFF everywhere else. localStorage-overridable per
// session, which is how a domain gets flip-tested against ACH in prod.
export const achBackendEnabledDefault = (env: AppEnv): boolean => env === 'qa';

export type ApiHostForOptions = {
  apiHost: string;
  achApiHost: string;
  achBackendEnabled: boolean;
  migratedDomains?: readonly AchDomain[];
};

export const apiHostFor = (
  domain: AchDomain,
  { apiHost, achApiHost, achBackendEnabled, migratedDomains = ACH_MIGRATED_DOMAINS }: ApiHostForOptions
): string => {
  if (achBackendEnabled && migratedDomains.includes(domain)) {
    return achApiHost;
  }
  return apiHost;
};

// VITE_ACH_TESTER_EMAILS: comma-separated allowlist scoping who may flip the FE
// to ACH on prod. Unset/empty => dark. Split, trim, lowercase, drop empties.
export const parseAchTesterEmails = (raw: string | undefined | null): string[] =>
  (raw ?? '')
    .split(',')
    .map((email) => email.trim().toLowerCase())
    .filter((email) => email.length > 0);

// TRUE iff the allowlist is set and the user's (trimmed, case-insensitive) email
// is on it. This is UX scoping, NOT a security boundary — ACH backend authz is.
export const achTesterEligible = (
  userEmail: string | undefined | null,
  allowlistRaw: string | undefined | null
): boolean => {
  const allowlist = parseAchTesterEmails(allowlistRaw);
  if (allowlist.length === 0) return false;
  // typeof guard: a tampered localStorage user blob may hold a non-string email.
  const email = typeof userEmail === 'string' ? userEmail.trim().toLowerCase() : '';
  return email.length > 0 && allowlist.includes(email);
};

// Outside DEV, the stored gate is honored only for an allowlisted tester;
// otherwise the env default stands, so a stray localStorage key does nothing.
// DEV honors the stored gate as-is (preserves the e2e harness).
export const effectiveAchBackendEnabled = (params: {
  isDev: boolean;
  storedEnabled: boolean;
  envDefault: boolean;
  userEmail: string | undefined | null;
  allowlistRaw: string | undefined | null;
}): boolean => {
  const { isDev, storedEnabled, envDefault, userEmail, allowlistRaw } = params;
  if (isDev) return storedEnabled;
  return achTesterEligible(userEmail, allowlistRaw) ? storedEnabled : envDefault;
};

// TEST-ONLY flip seam. The set of migrated domains is a checked-in const
// (ACH_MIGRATED_DOMAINS) so a production flip is a code change + review. The e2e
// live-test harness (e2e/) needs to flip a domain ON for a single run WITHOUT a
// rebuild, so it seeds a `e2eMigratedDomains` localStorage array which useConfig
// threads through here.
//
// This is gated to DEV builds only: in a PROD bundle the override is ignored and
// resolution falls back to the checked-in list, so production behaviour is
// byte-for-byte unchanged and the localStorage key is inert. `isDev` is passed in
// (import.meta.env.DEV at the call site) to keep this a pure, unit-testable fn.
export const resolveMigratedDomains = (override: unknown, isDev: boolean): readonly AchDomain[] => {
  if (isDev && Array.isArray(override)) {
    return override.filter((d): d is AchDomain => typeof d === 'string');
  }
  return ACH_MIGRATED_DOMAINS;
};
